import { useState, useEffect, lazy, Suspense } from "react";
import { useAuth } from "./hooks/useAuth.js";
import { auth } from "./firebase.js";
import { C } from "./utils/palette.js";
import { emptyChild, POZYCJE, getWiek } from "./utils/constants.js";
import {
  getPlayer, createPlayer, deletePlayer,
  getMyPlayerIds, getMyTeamIds, getTeam, getMe,
  authLogout, authResetPassword,
} from "./storage.js";
import { isPushEligible, requestPushPermissionAndRegister, initForegroundToasts, refreshPushTokenIfEnabled } from "./services/push.js";
import { css } from "./styles/global.js";
// Leniwie, bo do rejestracji i logowania ten kod jest niepotrzebny, a ważył
// większość pierwszego pakietu — razem z Footballingo (62 lekcje) trzeba było
// pobrać i sparsować całą aplikację, zanim narysował się formularz rejestracji.
// Oba chunki wchodzą dopiero po ustaleniu roli, więc rodzic nigdy nie pobiera
// panelu trenera i odwrotnie.
import BugReporter  from "./components/BugReporter.jsx";

const CoachApp  = lazy(() => import("./coach/CoachApp.jsx"));
const PlayerApp = lazy(() => import("./player/PlayerApp.jsx"));

// Wizualnie ten sam ekran co showStatus() w main.jsx — przejście z bootowania
// aplikacji w dociąganie chunku ma być niezauważalne, bez mignięcia tła.
function Ladowanie() {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", background: C.bg,
      fontFamily: "'Segoe UI', sans-serif", padding: 20, textAlign: "center",
    }}>
      <span className="logo-word" style={{ fontSize: 28 }}>FootL<span className="logo-ball"></span>g</span>
    </div>
  );
}

const childOf = id => getPlayer(id) || emptyChild();

/**
 * Składa obiekt trenera dla CoachApp z dwóch źródeł: profilu /users/{uid}
 * oraz dokumentów drużyn wskazanych claimem coachTeams.
 *
 * W v1 wszystko siedziało w jednym dokumencie coach_{id} razem z tablicą
 * teams[]. Rozdzielenie jest wymuszone regułami: drużyna musi być osobnym
 * dokumentem, żeby dało się zapytać o jej skład po teamId i przypiąć coachUid.
 */
function buildCoachData(profile, teamIds) {
  const ids = teamIds || [];
  return {
    me: profile,
    uid: profile?.uid || null,
    name: profile?.displayName || "",
    email: profile?.email || "",
    teamIds: ids,
    teams: ids.map(id => ({ teamId: id, ...(getTeam(id) || {}) })),
  };
}

export default function App() {
  // ── Stan konta ─────────────────────────────────────────────────────────────
  // `me` to dokument /users/{uid}: rola, email, nazwa. Lista zawodników NIE
  // jest jego polem — bierze się z zapytania where("ownerUid","==",uid), więc
  // trzymamy ją osobno i odświeżamy po `fl-sync-complete`.
  const [me,        setMe]                  = useState(null);
  const [playerIds, setPlayerIds]           = useState([]);
  const [childId,   setChildId]             = useState(null);
  const [addingChild, setAddingChild]       = useState(false);
  const [cf, setCf]                         = useState({ name: "", klub: "", pozycja: "Pomocnik", numer: "", wiek: "15" });
  const [playerErr, setPlayerErr]           = useState("");

  // ── Stan trenera ───────────────────────────────────────────────────────────
  const [coachData, setCoachData]           = useState(null);
  // Drużyna właśnie założona przy rejestracji — jednorazowy powitalny toast
  // o pilocie (docs/PAYMENTS-PLAN.md §6.2), czytany przez CoachApp tylko przy
  // montowaniu. `null` dla zwykłego logowania (routeTo nie ustawia newTeam).
  const [newCoachTeam, setNewCoachTeam]     = useState(null);

  // ── A2HS banner (Add to Home Screen) ───────────────────────────────────────
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installOS,         setInstallOS]         = useState("android");

  function checkAndShowInstall() {
    // Zwraca true jeśli banner został pokazany — używane do wykluczenia
    // jednoczesnego push-promptu w tym samym wywołaniu onInstallCheck (patrz niżej).
    // 1. Mobile-only (< 768px)
    if (typeof window === "undefined" || window.innerWidth >= 768) return false;
    // 2. Skip if już zainstalowane (standalone)
    const standalone = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
    if (standalone) return false;
    // 3. Snooze 7 dni po kliknięciu "X"
    const dismissedAt = parseInt(localStorage.getItem("fl_install_dismissed_at") || "0", 10);
    if (dismissedAt && (Date.now() - dismissedAt) < 7 * 24 * 60 * 60 * 1000) return false;
    // 4. Licznik sesji — pokazuj od 2. logowania
    const count = parseInt(localStorage.getItem("fl_login_count") || "0", 10) + 1;
    localStorage.setItem("fl_login_count", String(count));
    if (count < 2) return false;
    // 5. Wykrycie platformy — iOS (Safari + Chrome/Firefox iOS — wszystkie WebKit)
    //    / Android (Chrome, Samsung Internet, Firefox itd.)
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS 13+
    const isAndroid = /Android/i.test(ua);
    if (!isIOS && !isAndroid) return false;
    setInstallOS(isIOS ? "ios" : "android");
    setShowInstallBanner(true);
    return true;
  }

  function dismissInstallBanner() {
    localStorage.setItem("fl_install_dismissed_at", String(Date.now()));
    setShowInstallBanner(false);
  }

  // ── Push notification prompt (miękki, custom UI PRZED natywnym promptem) ────
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [pushToast, setPushToast] = useState(null);

  function checkAndShowPushPrompt() {
    if (!isPushEligible()) return; // iOS non-standalone / brak wsparcia Push API
    if (typeof Notification === "undefined" || Notification.permission !== "default") return;
    const dismissedAt = parseInt(localStorage.getItem("fl_push_dismissed_at") || "0", 10);
    if (dismissedAt && (Date.now() - dismissedAt) < 7 * 24 * 60 * 60 * 1000) return;
    // Osobny licznik logowań (nie fl_login_count — ten jest gated mobile-only
    // przez checkAndShowInstall i nigdy by nie rósł na desktopie, gdzie push
    // też ma działać).
    const count = parseInt(localStorage.getItem("fl_push_login_count") || "0", 10) + 1;
    localStorage.setItem("fl_push_login_count", String(count));
    if (count < 2) return;
    setShowPushBanner(true);
  }

  function dismissPushBanner() {
    localStorage.setItem("fl_push_dismissed_at", String(Date.now()));
    setShowPushBanner(false);
  }

  async function enablePush() {
    setShowPushBanner(false);
    const fbUid = auth.currentUser?.uid;
    if (!fbUid) return;
    const role = screen === "coach" ? "coach" : "parent";
    const playerIds = role === "parent" && childId ? [childId] : [];
    const ok = await requestPushPermissionAndRegister(fbUid, { role, playerIds });
    if (!ok) console.warn("[FL] push: użytkownik odrzucił prompt lub rejestracja nie powiodła się");
  }

  // ── Auth hook ──────────────────────────────────────────────────────────────
  const {
    screen, setScreen,
    sessionRestoring,
    err, setErr,
    lf, setLf,
    showForgot, setShowForgot,
    forgotEmail, setForgotEmail,
    forgotOk, setForgotOk,
    rf, setRf,
    regRole, setRegRole,
    coachForm, setCoachForm,
    acceptedTerms, setAcceptedTerms,
    login, register, registerCoach,
    // Jedna funkcja dla obu ról — w v1 były dwie, bo klient sam kasował
    // dokumenty i musiał zdążyć przed unieważnieniem tokena.
    deleteFullAccount,
    vf, setVf, pendingEmail, resendCooldownUntil,
    confirmCode, resendCode, cancelVerification,
  } = useAuth({
    onParentLogin: ({ me: profile, playerIds: ids, addingChild: ac }) => {
      setMe(profile); setPlayerIds(ids); setAddingChild(ac);
      // ── Auto-select: pomijamy ekran wyboru, gdy zawodnik już jest ────────
      if (!ac && ids.length > 0) {
        const lastPicked = localStorage.getItem("fl_last_child_" + (profile?.uid || ""));
        const pick = (lastPicked && ids.includes(lastPicked)) ? lastPicked : ids[0];
        setChildId(pick);
        return "app";
      }
      return "players";
    },
    onCoachLogin: ({ me: profile, teamIds, newTeam }) => {
      setMe(profile);
      setCoachData(buildCoachData(profile, teamIds));
      // newTeam tylko z confirmCode (rejestracja) — zwykłe logowanie (routeTo)
      // go nie przekazuje, więc jawnie czyścimy, inaczej toast z poprzedniej
      // rejestracji w tej samej karcie wróciłby po wylogowaniu i zalogowaniu.
      setNewCoachTeam(newTeam || null);
    },
    onLogout: () => { setMe(null); setPlayerIds([]); setChildId(null); setCoachData(null); setNewCoachTeam(null); },
    onInstallCheck: () => {
      const installShown = checkAndShowInstall();
      if (!installShown) checkAndShowPushPrompt();
    },
  });

  // Sync w tle zakończony → odśwież listę zawodników i drużyn z cache'u.
  // Listenery onSnapshot pomijają pierwszy snapshot, więc zmiana powstała przy
  // zamkniętej aplikacji (trener dopisał materiał, zawodnik doszedł na innym
  // urządzeniu) nie obudziłaby ekranu bez tego zdarzenia.
  useEffect(() => {
    const onSync = () => {
      setPlayerIds(getMyPlayerIds());
      // Czytamy ŚWIEŻY stan z cache'u, nie `prev` — loadMyData mogło właśnie
      // dociągnąć drużynę założoną na innym urządzeniu. Przebudowa z prev.teamIds
      // zamrażałaby listę na tej, którą znaliśmy przy logowaniu.
      setCoachData(prev => (prev ? buildCoachData(getMe() || prev.me, getMyTeamIds()) : prev));
    };
    window.addEventListener("fl-sync-complete", onSync);
    return () => window.removeEventListener("fl-sync-complete", onSync);
  }, []);

  // Odświeżenie tokena push po zalogowaniu — dla OBU ról, bo baner włączający
  // powiadomienia działa też u trenera (enablePush wyżej ustawia role z `screen`),
  // a trener nie ma własnego przełącznika w ustawieniach, więc nie ma żadnej
  // innej ścieżki, która by mu ten token odnowiła.
  //
  // Sama funkcja jest dławiona do raz na dobę i wychodzi natychmiast, gdy
  // powiadomienia nie są na tym urządzeniu włączone — tutaj zostaje wyłącznie
  // decyzja „kto i z jakimi playerIds".
  useEffect(() => {
    if (screen !== "app" && screen !== "coach") return;
    const fbUid = auth.currentUser?.uid;
    if (!fbUid) return;
    const role = screen === "coach" ? "coach" : "parent";
    refreshPushTokenIfEnabled(fbUid, {
      role,
      playerIds: role === "parent" && childId ? [childId] : [],
    });
  }, [screen, childId]);

  // Foreground toasts — appka aktywna na pierwszym planie, natywna notyfikacja
  // by się i tak nie pokazała, więc renderujemy lekki custom toast zamiast niej.
  useEffect(() => {
    if (screen !== "app" && screen !== "coach") return;
    let unsub = () => {};
    let cancelled = false;
    initForegroundToasts(({ title, body, url }) => {
      setPushToast({ title, body, url });
      setTimeout(() => setPushToast(null), 6000);
    }).then(u => { if (!cancelled) unsub = u; });
    return () => { cancelled = true; unsub(); };
  }, [screen]);

  // ── Wybór zawodnika ────────────────────────────────────────────────────────
  function selectChild(id) {
    setChildId(id);
    // Zapamiętanie wyboru — przy następnym logowaniu auto-select trafia w ostatniego
    if (me?.uid) localStorage.setItem("fl_last_child_" + me.uid, id);
    setScreen("app");
  }

  async function saveNewChild() {
    if (!cf.name) { setPlayerErr("Wpisz imię zawodnika"); return; }
    // Reguła: 1 email = 1 zawodnik. Jeśli konto ma już profil — blokujemy.
    if (playerIds.length > 0) {
      setPlayerErr("To konto ma już zawodnika. Każdy profil wymaga osobnego emaila.");
      return;
    }
    try {
      // createPlayer czeka na potwierdzenie zapisu, bo zaraz po nim skaczemy do
      // panelu zawodnika — a ten czyta dokument z cache'u.
      const id = await createPlayer(emptyChild(cf));
      setPlayerIds(getMyPlayerIds());
      setCf({ name: "", klub: "", pozycja: "Pomocnik", numer: "", wiek: "15" });
      setAddingChild(false); setPlayerErr("");
      selectChild(id);
    } catch (e) {
      setPlayerErr(e.message || "Błąd zapisu. Sprawdź połączenie i spróbuj ponownie.");
    }
  }

  // Kasowanie idzie przez CF: poza samym dokumentem zawodnika trzeba usunąć
  // raporty pod drużyną (klient nie ma do nich prawa zapisu) i zdjąć claim
  // memberTeams, jeśli to było ostatnie dziecko w tej drużynie.
  async function handleDeleteChild(id) {
    if (!window.confirm("Usunąć profil zawodnika? Tej akcji nie można cofnąć.")) return;
    try {
      await deletePlayer(id);
      setPlayerIds(getMyPlayerIds());
      if (childId === id) { setChildId(null); setScreen("players"); }
    } catch (e) {
      setPlayerErr(e.message || "Nie udało się usunąć profilu.");
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      <style>{css}</style>

      {/* ── A2HS floating banner (mobile only) ── */}
      {showInstallBanner && (
        <div
          role="dialog"
          aria-label="Dodaj FootLog do ekranu głównego"
          style={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            zIndex: 99999,
            background: "#ffffff",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
            padding: "14px 40px 14px 14px",
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            maxWidth: 520,
            margin: "0 auto",
          }}
        >
          <button
            onClick={dismissInstallBanner}
            aria-label="Zamknij i ukryj na 7 dni"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 32,
              height: 32,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
              color: "#6b7280",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>⚽</span>
            Dodaj FootLog do ekranu głównego
          </div>

          <div style={{ fontSize: 12.5, color: "#4b5563", marginBottom: 10, lineHeight: 1.45 }}>
            Działa jak zwykła aplikacja z {installOS === "ios" ? "App Store" : "Google Play"} – miej FootLog zawsze pod ręką!
          </div>

          {installOS === "ios" ? (
            <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.55 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontWeight: 700, minWidth: 14 }}>1.</span>
                <span>
                  Kliknij ikonę <b>„Udostępnij"</b>{" "}
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ verticalAlign: "-3px", margin: "0 2px" }}
                  >
                    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  (kwadrat ze strzałką na dole).
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 4 }}>
                <span style={{ fontWeight: 700, minWidth: 14 }}>2.</span>
                <span>Wybierz z listy: <b>„Dodaj do ekranu głównego"</b>.</span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.55 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontWeight: 700, minWidth: 14 }}>1.</span>
                <span>
                  Kliknij ikonę <b>trzech kropek</b>{" "}
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#111827"
                    style={{ verticalAlign: "-3px", margin: "0 2px" }}
                  >
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                  (prawy górny róg).
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 4 }}>
                <span style={{ fontWeight: 700, minWidth: 14 }}>2.</span>
                <span>Wybierz: <b>„Dodaj do ekranu głównego"</b> lub <b>„Zainstaluj aplikację"</b>.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Push notification banner (miękki prompt, przed natywnym) ── */}
      {showPushBanner && (
        <div
          role="dialog"
          aria-label="Włącz powiadomienia"
          style={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            zIndex: 99999,
            background: "#ffffff",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
            padding: "14px 16px",
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            maxWidth: 520,
            margin: "0 auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>🔔</span>
            Włącz powiadomienia
          </div>
          <div style={{ fontSize: 12.5, color: "#4b5563", marginBottom: 12, lineHeight: 1.45 }}>
            Dostaniesz info o nowym raporcie trenera i przypomnienie, gdy Twoja seria Footballingo zaraz wygaśnie.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={enablePush}>Włącz</button>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={dismissPushBanner}>Nie teraz</button>
          </div>
        </div>
      )}

      {/* ── Push foreground toast ── */}
      {pushToast && (
        <div
          role="status"
          style={{
            position: "fixed",
            top: "calc(12px + env(safe-area-inset-top, 0px))",
            left: 12,
            right: 12,
            zIndex: 99999,
            background: "#ffffff",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
            padding: "12px 14px",
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            maxWidth: 520,
            margin: "0 auto",
            cursor: "pointer",
          }}
          onClick={() => { if (pushToast.url) window.location.href = pushToast.url; setPushToast(null); }}
        >
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 2 }}>{pushToast.title}</div>
          {pushToast.body && <div style={{ fontSize: 12.5, color: "#4b5563" }}>{pushToast.body}</div>}
        </div>
      )}

      {/* ── Session restore splash ── */}
      {sessionRestoring && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.bg, fontFamily: "'Segoe UI',sans-serif" }}>
          <span className="logo-word" style={{ fontSize: 28 }}>FootL<span className="logo-ball"></span>g</span>
        </div>
      )}

      {/* ── Login ── */}
      {!sessionRestoring && screen === "login" && (
        <div className="auth-wrap"><div className="auth-box">
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            <span className="logo-word" style={{ fontSize: 42 }}>FootL<span className="logo-ball"></span>g</span>
          </div>
          <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 400, fontSize: 15, color: C.muted, marginBottom: 28, lineHeight: 1.4 }}>Forma to proces. Monitoruj to, co najważniejsze.</div>
          {err && <div className="err">{err}</div>}
          <div className="field"><label>Email</label><input placeholder="adres@email.com" value={lf.email} onChange={e => setLf({ ...lf, email: e.target.value })} /></div>
          <div className="field"><label>Hasło</label><input type="password" placeholder="••••••••" value={lf.pass} onChange={e => setLf({ ...lf, pass: e.target.value })} onKeyDown={e => e.key === "Enter" && login()} /></div>
          <button className="btn btn-primary" onClick={login}>Zaloguj się →</button>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => { setShowForgot(true); setForgotEmail(lf.email); setForgotOk(""); setErr(""); }}>
              Zapomniałem hasła
            </button>
          </div>
          {showForgot && (
            <div style={{ marginTop: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 13, color: C.text, marginBottom: 8, fontWeight: 600 }}>Reset hasła</div>
              {forgotOk
                ? <div style={{ fontSize: 13, color: C.green, lineHeight: 1.5 }}>✅ {forgotOk}</div>
                : <>
                    <input style={{ width: "100%", marginBottom: 8 }} placeholder="Twój email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
                    <button className="btn btn-ghost btn-sm" style={{ width: "100%" }} onClick={async () => {
                      try { await authResetPassword(forgotEmail); setForgotOk("Email z linkiem do zmiany hasła został wysłany. Sprawdź skrzynkę."); } catch(e) { setErr(e.message); }
                    }}>Wyślij link →</button>
                  </>
              }
            </div>
          )}
          <div className="auth-switch">Nowe konto? <button onClick={() => { setScreen("register"); setErr(""); setRegRole("parent"); setShowForgot(false); setAcceptedTerms(false); }}>Zarejestruj się</button></div>
        </div></div>
      )}

      {/* ── Register ── */}
      {screen === "register" && (
        <div className="auth-wrap"><div className="auth-box" style={{ maxWidth: 440 }}>
          <div className="auth-logo">FootL<span className="logo-ball"></span>g</div>
          <div className="auth-title">Nowe<br /><span>konto</span></div>
          {err && <div className="err">{err}</div>}

          <div className="role-tabs">
            <div className={`role-tab ${regRole === "parent" ? "active" : ""}`} onClick={() => { setRegRole("parent"); setErr(""); }}>
              <div className="role-tab-icon">👨‍👦</div>
              <div className="role-tab-label">Zawodnik</div>
            </div>
            <div className={`role-tab ${regRole === "coach" ? "active" : ""}`} onClick={() => { setRegRole("coach"); setErr(""); }}>
              <div className="role-tab-icon">📋</div>
              <div className="role-tab-label">Trener</div>
              <div className="role-tab-sub">Panel drużyny</div>
            </div>
          </div>

          {regRole === "parent" ? (
            <>
              <div className="field"><label>Email</label><input placeholder="adres@email.com" value={rf.email} onChange={e => setRf({ ...rf, email: e.target.value })} /></div>
              <div className="field"><label>Hasło</label><input type="password" placeholder="min. 6 znaków" value={rf.pass} onChange={e => setRf({ ...rf, pass: e.target.value })} /></div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Konto trenera daje dostęp do panelu drużyny. Zawodnicy dołączają przez unikalny kod.</div>
              <div className="field"><label>Imię i nazwisko trenera</label><input placeholder="np. Jan Kowalski" value={coachForm.name} onChange={e => setCoachForm({ ...coachForm, name: e.target.value })} /></div>
              <div className="field"><label>Nazwa akademii / klubu</label><input placeholder="np. Juventus Academy" value={coachForm.academy} onChange={e => setCoachForm({ ...coachForm, academy: e.target.value })} /></div>
              <div className="field"><label>Rocznik drużyny</label><input placeholder="np. 2010" type="number" min="2000" max="2020" value={coachForm.birthYear} onChange={e => setCoachForm({ ...coachForm, birthYear: e.target.value })} /></div>
              <div className="field"><label>Email</label><input placeholder="adres@email.com" value={coachForm.email} onChange={e => setCoachForm({ ...coachForm, email: e.target.value })} /></div>
              <div className="field"><label>Hasło</label><input type="password" placeholder="min. 6 znaków" value={coachForm.pass} onChange={e => setCoachForm({ ...coachForm, pass: e.target.value })} /></div>
            </>
          )}

          {/* Zgoda i przycisk są wspólne dla obu ról, dlatego stoją POZA ternary —
              zduplikowane w obu gałęziach rozjechałyby się przy pierwszej zmianie
              treści. Linki zatrzymują propagację: bez stopPropagation kliknięcie
              w „Regulamin" przełączałoby też checkbox, bo <label> przekazuje
              kliknięcie do swojego pola. Nowa karta jest konieczna — nawigacja
              w tej samej skasowałaby wpisany email i hasło, bo formularz żyje
              wyłącznie w stanie komponentu. */}
          <label className="consent-row">
            <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} />
            <span>
              Akceptuję <a href="/regulamin.html" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Regulamin FootLog</a>
              {" "}i potwierdzam zapoznanie się z <a href="/prywatnosc.html" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Polityką Prywatności</a>.
            </span>
          </label>

          <button className="btn btn-primary" onClick={regRole === "parent" ? register : registerCoach}>
            {regRole === "parent" ? "Utwórz konto →" : "Utwórz konto trenera →"}
          </button>

          <div className="auth-switch">Masz konto? <button onClick={() => { setScreen("login"); setErr(""); }}>Zaloguj się</button></div>
        </div></div>
      )}

      {/* ── Weryfikacja email + kod ── */}
      {screen === "verify-code" && (
        <div className="auth-wrap"><div className="auth-box">
          <div className="auth-logo">FootL<span className="logo-ball"></span>g</div>
          <div className="auth-title">Potwierdź<br /><span>adres email</span></div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
            Wysłaliśmy 6-cyfrowy kod na <strong style={{ color: C.text }}>{pendingEmail}</strong>.
            Wpisz go poniżej — kod jest ważny 10 minut.
          </div>
          {err && <div className="err">{err}</div>}
          <div className="field">
            <label>Kod weryfikacyjny</label>
            <input
              inputMode="numeric" maxLength={6} placeholder="123456"
              style={{ letterSpacing: 6, fontSize: 20, textAlign: "center" }}
              value={vf.code}
              onChange={e => setVf({ code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              onKeyDown={e => e.key === "Enter" && confirmCode()}
            />
          </div>
          <button className="btn btn-primary" onClick={confirmCode}>Potwierdź →</button>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={Date.now() < resendCooldownUntil}
              onClick={resendCode}
            >
              {Date.now() < resendCooldownUntil ? "Wyślij kod ponownie (odczekaj chwilę)" : "Wyślij kod ponownie"}
            </button>
          </div>
          <div className="auth-switch">
            <button onClick={cancelVerification}>← Anuluj i wróć do logowania</button>
          </div>
        </div></div>
      )}

      {/* ── Coach ── */}
      {screen === "coach" && coachData && (
        <Suspense fallback={<Ladowanie />}>
          <CoachApp
            coachData={coachData}
            setCoachData={setCoachData}
            deleteCoachAccount={deleteFullAccount}
            newTeam={newCoachTeam}
          />
        </Suspense>
      )}

      {/* ── Player select / add ── */}
      {screen === "players" && (
        <div className="players-wrap"><div className="players-box">
          <div className="players-hdr">FootL<span className="logo-ball"></span>g</div>
          {addingChild ? (
            <>
              {/* Sekcja „Odzyskaj zawodnika" zniknęła razem z pojęciem sieroty.
                  W kv zawodnik był osiągalny wyłącznie przez account_.children,
                  więc rozjazd tej listy odcinał go od właściciela. Teraz każdy
                  dokument nosi ownerUid i wraca zapytaniem — nie ma czego
                  odzyskiwać. */}
              <div className="players-title">Dodaj <span>zawodnika</span></div>
              {playerErr && <div className="err">{playerErr}</div>}
              <div className="form-2" style={{ marginBottom: 14 }}>
                <div className="field" style={{ marginBottom: 0 }}><label>Imię i nazwisko *</label><input placeholder="Jan Kowalski" value={cf.name} onChange={e => setCf({ ...cf, name: e.target.value })} /></div>
                <div className="field" style={{ marginBottom: 0 }}><label>Klub</label><input placeholder="nazwa klubu" value={cf.klub} onChange={e => setCf({ ...cf, klub: e.target.value })} /></div>
              </div>
              <div className="form-3" style={{ marginBottom: 14 }}>
                <div className="field" style={{ marginBottom: 0 }}><label>Wiek</label>
                  <select value={cf.wiek} onChange={e => setCf({ ...cf, wiek: e.target.value })}>
                    {[10,11,12,13,14,15,16,17,18].map(a => <option key={a} value={a}>{a} lat</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}><label>Pozycja</label>
                  <select value={cf.pozycja} onChange={e => setCf({ ...cf, pozycja: e.target.value })}>
                    {POZYCJE.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}><label>Numer</label>
                  <input type="number" placeholder="np. 10" value={cf.numer} onChange={e => setCf({ ...cf, numer: e.target.value })} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveNewChild}>Dodaj zawodnika →</button>
                {playerIds.length > 0 && <button className="btn btn-ghost" onClick={() => { setAddingChild(false); setPlayerErr(""); }}>Anuluj</button>}
              </div>
              {/* Wyjście z onboardingu — inaczej user po rejestracji/refreshu utknąłby na formularzu */}
              {playerIds.length === 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 12 }}>
                  <button
                    style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 0 }}
                    onClick={async () => { await authLogout(); window.location.reload(); }}>
                    ← Wyloguj
                  </button>
                  <button
                    style={{ background: "none", border: "none", color: C.red, cursor: "pointer", padding: 0, opacity: 0.8 }}
                    onClick={deleteFullAccount}>
                    Usuń konto
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="players-title">Wybierz <span>zawodnika</span></div>
              {playerIds.map(id => {
                const c = childOf(id);
                return (
                  <div key={id} className="player-card" onClick={() => selectChild(id)}>
                    <div className="player-avatar">{c.numer || c.name?.[0] || "?"}</div>
                    <div className="player-info">
                      <div className="player-name">{c.name}</div>
                      <div className="player-sub">{c.pozycja}{c.klub ? ` · ${c.klub}` : ""} · {getWiek(c)} lat</div>
                    </div>
                    <div style={{ fontSize: 18, color: C.muted, flexShrink: 0 }}>›</div>
                  </div>
                );
              })}
              <div style={{ display: "flex", justifyContent: playerIds.length === 0 ? "space-between" : "flex-end", marginTop: 12 }}>
                {/* "+ Dodaj zawodnika" tylko gdy konto puste (onboarding po usunięciu profilu).
                    Zasada: 1 email = 1 zawodnik — nowy zawodnik wymaga osobnego konta. */}
                {playerIds.length === 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setAddingChild(true); setPlayerErr(""); }}>+ Dodaj zawodnika</button>
                )}
                <button style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer" }}
                  onClick={async () => { await authLogout(); window.location.reload(); }}>Wyloguj</button>
              </div>
            </>
          )}
        </div></div>
      )}

      {/* ── Zgłaszanie błędów (tylko zalogowani) ── */}
      {me && (
        <BugReporter
          userType={me.role === "coach" ? "trener" : "rodzic"}
          userId={me.uid}
        />
      )}

      {/* ── Panel zawodnika ── */}
      {screen === "app" && childId && (
        <Suspense fallback={<Ladowanie />}>
          <PlayerApp
            key={childId}
            childId={childId}
            me={me}
            deleteFullAccount={deleteFullAccount}
            deleteChild={handleDeleteChild}
          />
        </Suspense>
      )}
    </div>
  );
}
